import React from 'react';
import { EyeIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { ComparisonTable } from './ComparisonTable';
import { Testimonial } from './Testimonial';
import { CTAButton } from './CTAButton';
import { StickyCTA } from './StickyCTA';
import { UrgencyBanner } from './UrgencyBanner';
import { ImageSection } from './ImageSection';
import { useLocation } from '../hooks/useLocation';
export const Advertorial = () => {
  const {
    city,
    isLoading
  } = useLocation();
  // Function to replace New York with user's city while preserving capitalization
  const localizeText = (text: string): string => {
    if (isLoading) return text;
    return text.replace(/Athens/g, city).replace(/ATHENS/g, city.toUpperCase()).replace(/athens/g, city.toLowerCase());
  };
  return <div className="flex flex-col min-h-screen w-full bg-white" data-id="element-6">
      <Header data-id="element-7" />
      <div className="w-full bg-gray-50" data-id="element-8">
        <div className="text-xs font-semibold text-gray-500 py-2 text-center border-b border-gray-200" data-id="element-9">
          WOMEN'S HEALTH | Advertorial
        </div>
        {/* Urgency Banner */}
        <UrgencyBanner data-id="element-10" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6 bg-white" data-id="element-11">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-12">
            <main className="flex-grow w-full lg:w-2/3" data-id="element-13">
              {/* Main headline */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4" data-id="element-14">
                {localizeText('Athens Lymphatic Specialists Reveal: The Hidden Reason Your Lipedema Treatments Stop Working After a Few Hours (And What Provides Lasting Relief)')}
              </h1>
              {/* Subheadline */}
              <p className="text-base md:text-lg text-gray-700 mb-4 sm:mb-6" data-id="element-15">
                New research explains why $100+ treatments only provide
                temporary relief—and {localizeText('Athens')} specialists are
                recommending a breakthrough alternative that costs a fraction of
                recurring sessions.
              </p>
              {/* Author byline */}
              <div className="flex items-center justify-between text-sm text-gray-600 mb-4 sm:mb-6 border-b border-gray-200 pb-3 sm:pb-4" data-id="element-16">
                <span className="text-xs sm:text-sm" data-id="element-17">
                  By Jennifer Martinez, Women's Health | April 29, 2025
                </span>
                <span className="flex items-center text-xs sm:text-sm" data-id="element-18">
                  <span data-id="element-19">312,847</span>
                  <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" data-id="element-20" />
                </span>
              </div>
              {/* Featured Image */}
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/9vX9LnAAZUrcVNvUQgV8L6/pasted-image.png" alt="Woman measuring her leg with a measuring tape, struggling with lipedema" caption="Many women with lipedema struggle to find effective solutions for their condition" data-id="element-21" />
              {/* Article introduction */}
              <p className="mb-4 sm:mb-6" data-id="element-22">
                If you've been paying for professional lymphatic treatments for
                your lipedema—only to watch the relief disappear within
                hours—you're not alone.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-23">
                In fact, you're experiencing exactly what thousands of other
                women report: treatments work temporarily, then symptoms return,
                and you're right back where you started.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-24">
                According to lymphatic specialists in {localizeText('Athens')},
                there's a scientific reason why professional treatments only
                provide short-term relief—and why women are now turning to a
                more sustainable alternative that provides continuous support
                instead of periodic manual intervention.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-25">
                "We see women spending hundreds of dollars per session on
                lymphatic massage, manual drainage, and compression therapy,"
                says Dr. Rachel Chen, a leading lymphatic specialist at the{' '}
                {localizeText('Athens')} Center for Lymphatic Health. "The
                treatments provide immediate relief. But within a few hours to a
                couple days, the swelling returns, the heaviness comes back, and
                they need another appointment."
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-26">
                "They're not paying for a solution," Dr. Chen explains. "They're
                paying for a subscription to temporary relief."
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-27">
                But new research is revealing why this happens—and what actually
                provides lasting support for lipedema's compromised lymphatic
                system.
              </p>
              {/* Section: The Expensive Truth */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-28">
                The Expensive Truth About Professional Lipedema Treatments
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-29">
                Let's talk about what you're really spending.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-30">
                Professional lymphatic treatments for lipedema typically cost
                between $100 and $200 per session. And because the relief only
                lasts a few hours to a few days, you need recurring appointments
                to maintain any improvement.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-31">
                Even at just twice monthly, that's $200 to $400 per month. Over
                $2,400 to nearly $5,000 per year.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-32">
                For many women with lipedema, it's significantly more than that.
                Weekly sessions can easily exceed $6,000 to $10,000 annually.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-33">
                And what are you getting for that investment?
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-34">
                Temporary relief. Real, but temporary.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-35">
                The swelling reduces after your appointment. Your legs feel
                lighter. The discomfort eases. You feel hopeful.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-36">
                Then, within hours or days, it all returns. The heaviness comes
                back. The swelling reappears. You're back to baseline—or
                sometimes even worse.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-37">
                So you book another appointment. And another. And another.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-38">
                It's not a treatment plan. It's a dependency cycle that never
                ends.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/eCGcwekhgKdVToC7Uwq56x/ChatGPT_Image_11_%CE%9F%CE%BA%CF%84_2025%2C_04_40_54_%CE%BC.%CE%BC..png" alt="Uncomfortable compression marks from traditional stockings" caption="Traditional compression stockings often leave painful marks and can cause discomfort" width="3/4" data-id="element-39" />
              {/* Section: Why Treatments Stop Working */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-40">
                Why Your Treatments Stop Working After a Few Hours
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-41">
                Lymphatic researchers studied this pattern across thousands of
                treatment sessions to understand why professional treatments
                provide only temporary relief.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-42">Here's what they discovered:</p>
              <p className="mb-4 sm:mb-6" data-id="element-43">
                Professional treatments manually move trapped fluid through your
                compromised lymphatic system. Manual lymphatic drainage,
                compression therapy sessions, and specialized massage physically
                push fluid along your lymphatic pathways during the treatment.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-44">
                This provides immediate, measurable relief. Your legs feel
                lighter because fluid has been manually drained. The treatment
                works—in the moment.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-45">
                But here's the critical problem: These treatments don't address
                the underlying lymphatic dysfunction in lipedema. They manually
                drain fluid, but they don't support your lymphatic system's
                ability to drain continuously on its own.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-46">
                Think of it like this: if you have a sink with a clogged drain,
                manually bailing water out will empty the sink temporarily. But
                the moment you turn the water back on, it fills right back up
                because the drain still doesn't work properly.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-47">
                That's exactly what happens with professional treatments for
                lipedema.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/icymbux5wbm154SRSmarQe/ChatGPT_Image_11_%CE%9F%CE%BA%CF%84_2025%2C_04_40_55_%CE%BC.%CE%BC..png" alt="Diagram showing uniform pressure problem" caption="Traditional stockings apply uniform pressure, which doesn't address the unique needs of lipedema" width="2/3" data-id="element-48" />
              <p className="mb-4 sm:mb-6" data-id="element-49">
                The therapist manually drains fluid during your session. But
                once the treatment ends, your lymphatic system returns to its
                compromised baseline state. Fluid re-accumulates. Symptoms
                return. Usually within hours.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-50">
                You're caught in an endless cycle: Treatment provides relief →
                Relief fades → You need another treatment → Repeat indefinitely.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-51">
                Each cycle costing $100 to $200. Each providing only temporary
                benefit.
              </p>
              {/* Section: What Lipedema Actually Needs */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-52">
                What Lipedema's Lymphatic System Actually Needs
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-53">
                The same research that explained why manual treatments don't
                provide lasting relief also revealed what does work: continuous
                graduated compression specifically designed for lymphatic
                drainage.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-54">Here's the difference:</p>
              <p className="mb-4 sm:mb-6" data-id="element-55">
                Manual treatments provide periodic intervention. A therapist
                physically moves fluid for 60-90 minutes, then the treatment
                ends and your lymphatic system is on its own again.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-56">
                Continuous graduated compression provides sustained mechanical
                support throughout your entire day. Instead of periodic manual
                drainage, you get constant lymphatic assistance while the
                garment is worn.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/fJP5JtfucrJfsT1uk7QW3m/gempages_541671622348637232-7a3a123c-f1c8-4b25-b1a4-f051e570e74b_%283%29.webp" alt="Diagram of lymphatic pathways" caption="Effective compression should follow the body's natural lymphatic drainage pathways" width="2/3" data-id="element-57" />
              <p className="mb-4 sm:mb-6" data-id="element-58">
                According to {localizeText('Athens')} specialists, this is what
                lipedema's compromised lymphatic system actually needs: not
                periodic manual intervention, but continuous mechanical support
                that works with your body's natural drainage pathways all day
                long.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-59">
                Until recently, this kind of lipedema-specific continuous
                support didn't exist.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-60">But that's changed.</p>
              {/* Section: The Technology */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-61">
                The Technology That Provides Continuous Lymphatic Support
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-62">
                A company called Elivara has developed what{' '}
                {localizeText('Athens')} specialists are calling the first truly
                lipedema-specific compression technology—and it's designed to
                provide the continuous lymphatic support that manual treatments
                can't deliver sustainably.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-63">
                It's called FlowMapping™ Compression Technology.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/rLCAzcfebMhMNyKF4k1SA3/8.png" alt="Diagram showing graduated pressure zones in Elivara compression leggings" caption="Elivara's lipedema-specific design uses graduated pressure zones: light pressure at the top, moderate in the middle, and high pressure at the ankles to promote proper lymphatic flow" data-id="element-64" />
              <p className="mb-4 sm:mb-6" data-id="element-65">
                Instead of periodic manual drainage that stops when your
                appointment ends, FlowMapping™ provides continuous graduated
                compression that supports lymphatic drainage throughout your
                entire day.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-66">Here's how it works:</p>
              <p className="mb-4 sm:mb-6" data-id="element-67">
                <span className="font-semibold" data-id="element-68">Graduated Pressure Zones:</span>{' '}
                The compression is strongest at your ankles (where fluid tends
                to pool most severely) and gradually decreases as it moves up
                your leg. This creates a pressure gradient that constantly
                encourages trapped fluid to move upward—following your body's
                natural lymphatic pathways.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-69">
                <span className="font-semibold" data-id="element-70">Continuous Support:</span>{' '}
                Unlike a treatment session that lasts 60-90 minutes,
                FlowMapping™ works continuously while you wear it. All day,
                throughout your daily activities, the graduated compression
                provides sustained lymphatic support.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-71">
                <span className="font-semibold" data-id="element-72">Lipedema-Specific Design:</span>{' '}
                This isn't generic compression adapted for lipedema. Elivara
                designed FlowMapping™ specifically around lipedema's unique fat
                distribution patterns, tender zones, and lymphatic
                dysfunction—addressing the exact issues that make this condition
                different from other forms of swelling.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/fvse8ULQVXf6Qnk6sWPrcM/pasted-image.png" alt="Comparison diagram showing traditional compression versus FlowMapping technology" caption="Elivara's FlowMapping™ technology creates directional support that follows your lymphatic pathways, unlike traditional compression's uniform pressure approach" data-id="element-73" />
              <p className="mb-4 sm:mb-6" data-id="element-74">
                The technology addresses what researchers identified as the
                fundamental problem: your lymphatic system needs continuous
                support, not periodic manual drainage.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-75">
                "This is the difference between temporarily moving fluid
                manually and providing sustained mechanical support that works
                with your lymphatic system all day," says Dr. Chen, who now
                recommends Elivara to her patients. "It's not replacing the
                skill of manual drainage—it's replacing the need for recurring
                appointments by providing continuous support instead."
              </p>
              {/* Section: What It Looks Like */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-76">
                What It Looks Like in Practice
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-77">
                But here's what makes Elivara truly different from professional
                treatments:
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-78">
                It's integrated into comfortable, wearable leggings—not
                something requiring appointments, clinical visits, or
                professional administration.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-79">
                You put them on in the morning like regular leggings. The
                FlowMapping™ technology provides continuous graduated
                compression throughout your day. You go about your normal
                activities—work, errands, exercise, whatever your day
                involves—while the compression continuously supports your
                lymphatic drainage.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-80">
                No appointments to schedule. No recurring costs. No dependence
                on someone else's availability.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-81">
                The technology does what manual treatments do temporarily, but
                continuously and sustainably.
              </p>
              {/* Section: Testimonials */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-6" data-id="element-82">
                What Women Are Experiencing
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-83">
                Women who were spending thousands annually on professional
                treatments are reporting dramatically different results with
                continuous graduated compression.
              </p>
              <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-10" data-id="element-84">
                <Testimonial quote="I was spending $180 twice a month on lymphatic massage. That's over $4,000 a year for relief that lasted maybe two days. The massage helped temporarily, but I was constantly booking the next appointment because symptoms always came back. When I discovered Elivara, I was skeptical—how could leggings replace professional treatment? But the continuous support is completely different from periodic manual drainage. My legs feel consistently better throughout the day, not just for a few hours after an appointment. I've cut my massage appointments down to once every few months instead of twice monthly. The cost savings alone are incredible." author="Patricia G., 47, Brooklyn, NY" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/g6rBRoysUyEVEFDJFiVEnE/istockphoto-1180524344-612x612_%281%29.jpg" data-id="element-85" />
                <Testimonial quote="The relief from professional treatments was real but so temporary. Elivara provides support that actually lasts." author="Diana L., 52, Manhattan, NY" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/itD5PUebJe2STwyiveYzbu/istockphoto-1290775513-612x612_%281%29.jpg" data-id="element-86" />
                <Testimonial quote="I'd leave my lymphatic drainage appointments feeling amazing. Light legs, reduced swelling, so much relief. Then by the next morning, sometimes even that same evening, the heaviness was back. It was so frustrating to spend that much money for such temporary improvement. FlowMapping™ compression provides relief that sustains throughout my day. I'm not on the treatment treadmill anymore." author="Jennifer R., 38, Miami, FL" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/kMiNm3v7qtTQa8SgVVQanP/Screenshot_2025-09-30_at_00.02.40.png" data-id="element-87" />
                <Testimonial quote="My swelling stays down now instead of returning within hours." author="Nicole R., 39, Queens, NY" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/ktyrgyaucoXAJBEpygevN8/Screenshot_2025-09-30_at_00.02.43.png" data-id="element-88" />
                <Testimonial quote="After professional treatments, I'd watch my legs swell right back up. Sometimes I could literally see it happening—by evening, all the improvement from my morning appointment was gone. With continuous graduated compression, the swelling reduction is sustained. My legs don't balloon back up because I'm getting continuous lymphatic support, not just periodic drainage." author="Rebecca T., 45, Denver, CO" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/p82hupoTi6bS5hfqE4JSjZ/Beth_-_living_with_lipedema.webp" data-id="element-89" />
                <Testimonial quote="I'm saving over $3,000 a year and getting better results." author="Michelle K., 44, Long Island, NY" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/acEiFfQAqFoVbyHhX4St2Y/ChatGPT_Image_30_%CE%A3%CE%B5%CF%80_2025%2C_12_12_37_%CF%80.%CE%BC..png" data-id="element-90" />
                <Testimonial quote="I was doing weekly compression therapy sessions at $150 each. That's $7,800 per year. Elivara was a fraction of that cost—essentially the price of a few treatment sessions—and it provides continuous support instead of one hour per week. The financial relief alone has been life-changing, but the fact that it actually works better than recurring treatments makes it even more incredible." author="Lisa B., 49, Phoenix, AZ" profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/58eTTAmUZvPANHSwRVq2PQ/ChatGPT_Image_11_%CE%9F%CE%BA%CF%84_2025%2C_05_02_29_%CE%BC.%CE%BC..png" data-id="element-91" />
              </div>
              {/* Section: Comparison */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-92">
                The Comparison: Temporary vs. Continuous Support
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-93">
                Let's look at professional treatments versus continuous
                graduated compression side by side:
              </p>
              <ComparisonTable data-id="element-94" />
              <p className="mt-4 sm:mt-6 mb-4 sm:mb-6" data-id="element-95">
                The difference isn't just about cost—though the financial
                savings are significant.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-96">
                It's about temporary manual intervention versus sustained
                mechanical support.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/stpvy78Ka74JfhKzrApmz1/pasted-image.png" alt="Side-by-side comparison of woman without support showing discomfort versus woman with Elivara leggings showing comfort and support" caption="Elivara leggings provide all-day comfort, targeted support, and discreet style compared to living without proper compression" data-id="element-97" />
              <p className="mb-4 sm:mb-6" data-id="element-98">
                "Women tell me they wish they'd known about continuous graduated
                compression before spending years and thousands of dollars on
                treatments that only helped temporarily," says Dr. Chen. "The
                treatments aren't bad—they just aren't sustainable. You can't
                manually drain your lymphatic system every few hours for the
                rest of your life. But you can wear continuous graduated
                compression that provides sustained support."
              </p>
              {/* Section: Call to Action */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-99">
                Stop the Treatment Treadmill
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-100">
                Here's something important to know: because Elivara uses
                specialized FlowMapping™ technology and is designed
                specifically for lipedema, they can only produce limited
                quantities at a time.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-101">
                Right now, they're offering first-time customers an exclusive
                opportunity to save 40% off their order.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-102">
                This isn't a permanent discount. It's designed specifically to
                help women escape the expensive treatment cycle and access
                sustainable lymphatic support—without the premium recurring
                costs of professional sessions.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-103">
                But here's the reality: when women with lipedema discover they
                can stop paying hundreds of dollars per session for temporary
                relief and get continuous support instead, these sell out fast.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-104">
                Popular sizes often disappear within days, and then you have to
                wait weeks for the next production run.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-105">
                If you've been caught in the cycle of expensive recurring
                treatments, this is your opportunity to try a more sustainable
                alternative.
              </p>
              {/* Section: Conclusion */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-3 sm:mb-4" data-id="element-106">
                You Weren't Wrong to Try Treatments—But There's a Better Way
              </h2>
              <p className="mb-4 sm:mb-6" data-id="element-107">
                If you've been paying for professional lymphatic treatments and
                feeling frustrated that the relief never lasts, I want you to
                hear this clearly:
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-108">
                Professional treatments do work—temporarily.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-109">
                The therapists are skilled. The manual drainage is effective.
                The relief you feel is real.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-110">
                The problem isn't the treatments themselves. It's that periodic
                manual intervention can't provide the continuous support that
                lipedema's compromised lymphatic system needs.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-111">
                You can't book appointments every few hours. You can't spend
                thousands of dollars annually for relief that disappears by the
                next day. It's not sustainable—financially, logistically, or
                practically.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-112">
                But continuous graduated compression designed specifically for
                lipedema's lymphatic dysfunction? That is sustainable.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-113">
                Imagine waking up, putting on comfortable leggings, and having
                continuous lymphatic support throughout your entire day—not just
                for the 60-90 minutes you're in a treatment session.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-114">
                Imagine your legs feeling consistently lighter and less swollen,
                not just for a few hours after an appointment but throughout
                your daily activities.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-115">
                Imagine saving thousands of dollars annually while getting
                better, more sustained results.
              </p>
              <ImageSection src="https://uploadthingy.s3.us-west-1.amazonaws.com/fENsNgwMEFidRKe6A9GbhD/ChatGPT_Image_11_%CE%9F%CE%BA%CF%84_2025%2C_04_52_26_%CE%BC.%CE%BC..png" alt="Woman active and comfortable in Elivara leggings" caption="Elivara leggings allow women with lipedema to stay active and comfortable throughout the day" data-id="element-116" />
              <p className="mb-4 sm:mb-6" data-id="element-117">
                Imagine not being dependent on booking the next appointment,
                coordinating your schedule around treatment availability, or
                wondering when the relief will fade this time.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-118">
                That's what continuous graduated compression offers.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-119">
                And right now, you can try it at 40% off.
              </p>
              <p className="mb-4 sm:mb-6" data-id="element-120">
                Don't spend another year paying hundreds per session for
                temporary relief. Discover what provides sustained lymphatic
                support at a fraction of the cost—before your size sells out.
              </p>
              <div className="flex justify-center my-8 sm:my-10" data-id="element-121">
                <CTAButton data-id="element-122" />
              </div>
              <p className="text-center mb-4 sm:mb-6 font-medium" data-id="element-123">
                Stop the expensive treatment cycle. Try the sustainable
                alternative {localizeText('Athens')} specialists are
                recommending.
              </p>
              {/* Disclaimer */}
              <div className="text-xs text-gray-500 mt-8 sm:mt-10 mb-4 sm:mb-6 border-t border-gray-200 pt-4" data-id="element-124">
                <p className="mb-3 sm:mb-4" data-id="element-125">
                  This is an advertisement and not an actual news article, blog,
                  or consumer protection update. Individual results may vary.
                  The testimonials presented are from actual customers but may
                  not represent typical results.
                </p>
                <p className="mb-3 sm:mb-4" data-id="element-126">
                  About Us | Terms of Use | Privacy Policy
                </p>
                <p className="mb-3 sm:mb-4" data-id="element-127">
                  Trademarks utilized on our website belong to their respective
                  owners and no implied or expressed endorsement of our website
                  or services is intended. Through in-depth research and
                  experienced editors we provide feedback about products and
                  services. We are independently owned, and opinions expressed
                  here are our own. This website is an independent professional
                  comparison and review site supported by referral fees from the
                  sites and products featured.
                </p>
                <p data-id="element-128">©2025 Women's Health Insights. All Rights Reserved.</p>
              </div>
            </main>
            {/* CTA Section - Only visible on desktop */}
            <div className="hidden lg:block w-full lg:w-1/3 lg:sticky lg:top-8 self-start" data-id="element-129">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200" data-id="element-130">
                <div className="p-4" data-id="element-131">
                  <h3 className="text-center font-bold text-lg mb-4" data-id="element-132">
                    Instant Lipedema Relief or Money Back!
                  </h3>
                  <div className="mb-4" data-id="element-133">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3BFse8FnMG3SD5NNtv1DWX/pasted-image.png" alt="Elivara compression leggings with gradient compression technology" className="w-full rounded-md" data-id="element-134" />
                  </div>
                  <div className="flex items-center justify-center mb-2" data-id="element-135">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-136" />)}
                  </div>
                  <p className="text-center font-bold text-sm mb-1" data-id="element-137">
                    4.8 out of 5
                  </p>
                  <p className="text-center text-xs text-gray-600 mb-4" data-id="element-138">
                    3,791 customer ratings
                  </p>
                  <div className="space-y-1 mb-4" data-id="element-139">
                    <div className="flex items-center text-xs" data-id="element-140">
                      <span className="w-6 text-right mr-2" data-id="element-141">5★</span>
                      <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-142">
                        <div className="h-full bg-yellow-400 w-[90%]" data-id="element-143"></div>
                      </div>
                      <span className="w-8 text-right ml-2" data-id="element-144">90%</span>
                    </div>
                    <div className="flex items-center text-xs" data-id="element-145">
                      <span className="w-6 text-right mr-2" data-id="element-146">4★</span>
                      <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-147">
                        <div className="h-full bg-yellow-400 w-[7%]" data-id="element-148"></div>
                      </div>
                      <span className="w-8 text-right ml-2" data-id="element-149">7%</span>
                    </div>
                    <div className="flex items-center text-xs" data-id="element-150">
                      <span className="w-6 text-right mr-2" data-id="element-151">3★</span>
                      <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-152">
                        <div className="h-full bg-yellow-400 w-[2%]" data-id="element-153"></div>
                      </div>
                      <span className="w-8 text-right ml-2" data-id="element-154">2%</span>
                    </div>
                    <div className="flex items-center text-xs" data-id="element-155">
                      <span className="w-6 text-right mr-2" data-id="element-156">2★</span>
                      <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-157">
                        <div className="h-full bg-yellow-400 w-[0%]" data-id="element-158"></div>
                      </div>
                      <span className="w-8 text-right ml-2" data-id="element-159">0%</span>
                    </div>
                    <div className="flex items-center text-xs" data-id="element-160">
                      <span className="w-6 text-right mr-2" data-id="element-161">1★</span>
                      <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-162">
                        <div className="h-full bg-yellow-400 w-[1%]" data-id="element-163"></div>
                      </div>
                      <span className="w-8 text-right ml-2" data-id="element-164">1%</span>
                    </div>
                  </div>
                  <button onClick={() => window.location.href = 'https://tryelivara.com/pages/elivara-landing-page'} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded flex items-center justify-center uppercase text-sm tracking-wider transition-colors duration-200" data-id="element-165">
                    CHECK AVAILABILITY{' '}
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-166" />
                  </button>
                  <p className="text-center text-xs text-gray-600 mt-2" data-id="element-167">
                    Limited time offer - Fast FREE shipping across the US
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-168" />
      <StickyCTA data-id="element-169" />
    </div>;
};